import React, { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Renders the bot-protection widget and hands the resulting token back via
 * onVerify. If REACT_APP_TURNSTILE_SITE_KEY is not set, this renders nothing
 * and stays out of the way (so local/dev without keys still works).
 *
 * The site key is public — only the secret (server side) must stay private.
 */

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const turnstileEnabled = !!SITE_KEY;

let scriptPromise = null;
function loadTurnstileScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Turnstile script'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function Turnstile({ onVerify, onExpire, action }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Keep callbacks in a ref so the widget is rendered exactly once and isn't
  // torn down/re-created when the parent re-renders.
  const cbRef = useRef({});
  cbRef.current = { onVerify, onExpire };

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action,
          callback: (token) => cbRef.current.onVerify?.(token),
          'expired-callback': () => cbRef.current.onExpire?.(),
          'error-callback': () => cbRef.current.onExpire?.(),
        });
      })
      .catch((e) => console.warn('[Turnstile]', e.message));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* already gone */ }
      }
    };
  }, [action]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} data-theme="light" data-size="flexible"  style={{ marginTop: 4, marginBottom: 10 }} />;
}
